import log from '@/lib/logger';
import {useEffect, useState} from 'react';
import {Dialog, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Badge} from '@/components/tailwind/badge';
import {useAuth} from '@/context/use-auth';
import {useMembersTranslation} from '@/i18n/hooks';
import {
    BookingMemberItem,
    SpecialAssignmentBookingAuditLog,
    getBookingMembers,
    getBookingAuditLogs,
    getBookingDetail,
    SpecialAssignmentBookingDetail,
} from '@/services/special-assignment-services';
import {DataTable, sortableColumnHeader} from '@/components/data-table';
import {ColumnDef} from '@tanstack/react-table';
import {Text} from '@/components/tailwind/text';
import {formatDateTimeString} from '@/lib/utils';

interface SpecialAssignmentBookingDetailDialogProps {
    bookingId: string | null;
    onClose: () => void;
}

type TabKey = 'members' | 'auditLog';

const memberColumns: ColumnDef<BookingMemberItem>[] = [
    {
        accessorKey: 'memberNo',
        header: ({column}) => sortableColumnHeader(column, 'specialAssignments.detailDialog.headers.memberNo', 'members'),
        cell: ({row}) => <Text>{row.original.memberNo}</Text>,
    },
    {
        accessorKey: 'firstName',
        header: ({column}) => sortableColumnHeader(column, 'specialAssignments.detailDialog.headers.firstName', 'members'),
        cell: ({row}) => <Text>{row.original.firstName}</Text>,
    },
    {
        accessorKey: 'lastName',
        header: ({column}) => sortableColumnHeader(column, 'specialAssignments.detailDialog.headers.lastName', 'members'),
        cell: ({row}) => <Text>{row.original.lastName}</Text>,
    },
];

function buildAuditLogColumns(t: (key: string) => string): ColumnDef<SpecialAssignmentBookingAuditLog>[] {
    return [
        {
            accessorKey: 'createdAt',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.auditLog.headers.timestamp', 'members'),
            cell: ({row}) => <Text>{formatDateTimeString(row.original.createdAt)}</Text>,
        },
        {
            accessorKey: 'actionType',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.auditLog.headers.action', 'members'),
            cell: ({row}) => {
                const color = row.original.actionType === 'DELETED' ? 'red'
                    : row.original.actionType === 'EDITED' ? 'amber'
                    : 'green';
                const label = t(`specialAssignments.auditLog.actions.${row.original.actionType}`);
                return <Badge color={color}>{label}</Badge>;
            },
        },
        {
            accessorKey: 'description',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.auditLog.headers.description', 'members'),
            cell: ({row}) => <Text className="text-sm">{row.original.description}</Text>,
        },
        {
            accessorKey: 'createdByUsername',
            header: ({column}) => sortableColumnHeader(column, 'specialAssignments.auditLog.headers.user', 'members'),
            cell: ({row}) => <Text>{row.original.createdByUsername}</Text>,
        },
    ];
}

export default function SpecialAssignmentBookingDetailDialog({
    bookingId,
    onClose,
}: SpecialAssignmentBookingDetailDialogProps) {
    const {t} = useMembersTranslation();
    const {token} = useAuth();
    const auditLogColumns = buildAuditLogColumns(t);

    const [activeTab, setActiveTab] = useState<TabKey>('members');
    const [members, setMembers] = useState<BookingMemberItem[]>([]);
    const [auditLogs, setAuditLogs] = useState<SpecialAssignmentBookingAuditLog[]>([]);
    const [bookingDetail, setBookingDetail] = useState<SpecialAssignmentBookingDetail | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!bookingId || !token) return;

        setActiveTab('members');

        async function loadData() {
            setIsLoading(true);
            try {
                const [memberData, logData, detail] = await Promise.all([
                    getBookingMembers(token, bookingId!),
                    getBookingAuditLogs(token, bookingId!),
                    getBookingDetail(token, bookingId!),
                ]);
                setMembers(memberData);
                setAuditLogs(logData);
                setBookingDetail(detail);
            } catch (error) {
                log.error('Error loading booking details:', error);
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [bookingId, token]);

    function handleClose() {
        setMembers([]);
        setAuditLogs([]);
        setBookingDetail(null);
        onClose();
    }

    const tabClasses = (tab: TabKey) =>
        `px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === tab
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
        }`;

    return (
        <Dialog size="3xl" open={!!bookingId} onClose={handleClose}>
            <DialogTitle>
                <div className="flex items-center gap-2">
                    {t('specialAssignments.detailDialog.title')}
                    {bookingDetail?.isDeleted && (
                        <Badge color="red">{t('specialAssignments.history.cancelled')}</Badge>
                    )}
                </div>
            </DialogTitle>
            <DialogBody>
                {isLoading ? (
                    <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
                        {t('specialAssignments.detailDialog.loading')}
                    </p>
                ) : (
                    <>
                        <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                            <nav className="-mb-px flex gap-2">
                                <button className={tabClasses('members')} onClick={() => setActiveTab('members')}>
                                    {t('specialAssignments.detailDialog.tabs.members')}
                                </button>
                                <button className={tabClasses('auditLog')} onClick={() => setActiveTab('auditLog')}>
                                    {t('specialAssignments.detailDialog.tabs.auditLog')}
                                </button>
                            </nav>
                        </div>

                        {activeTab === 'members' && (
                            <DataTable columns={memberColumns} data={members}/>
                        )}

                        {activeTab === 'auditLog' && (
                            auditLogs.length === 0 ? (
                                <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
                                    {t('specialAssignments.auditLog.emptyState')}
                                </p>
                            ) : (
                                <DataTable columns={auditLogColumns} data={auditLogs}/>
                            )
                        )}
                    </>
                )}

                <div className="mt-6 flex justify-end">
                    <Button outline onClick={handleClose}>
                        {t('specialAssignments.detailDialog.close')}
                    </Button>
                </div>
            </DialogBody>
        </Dialog>
    );
}
